package com.castly.admin.ui.components

import androidx.compose.runtime.*
import com.castly.admin.ui.icons.IconLogOut
import com.castly.admin.ui.icons.IconMoon
import com.castly.admin.ui.icons.IconSun
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun Topbar(
    pageTitle: String,
    isDark: Boolean,
    onToggleTheme: () -> Unit,
    userEmail: String?,
    onLogout: () -> Unit,
    sidebarWidth: CSSpxValue = 240.px
) {
    val colors = AppTheme.colors
    var showUserMenu by remember { mutableStateOf(false) }

    Div({
        style {
            property("position", "fixed")
            property("top", "0")
            property("left", "$sidebarWidth")
            property("right", "0")
            height(64.px)
            backgroundColor(Color(colors.surface))
            property("border-bottom", "1px solid ${colors.border}")
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.SpaceBetween)
            padding(0.px, Spacing.xxl)
            property("z-index", "1025")
            themeTransition()
        }
    }) {
        // Left: page title
        Span({
            style {
                fontSize(Typography.xxl)
                fontWeight("700")
                color(Color(colors.text))
            }
        }) { Text(pageTitle) }

        // Right: actions
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.sm)
            }
        }) {
            // Theme toggle
            Button({
                onClick { onToggleTheme() }
                attr("aria-label", if (isDark) "Switch to light mode" else "Switch to dark mode")
                style {
                    background("none")
                    border(0.px)
                    cursor("pointer")
                    padding(Spacing.sm)
                    property("border-radius", BorderRadius.circle)
                    color(Color(colors.textMuted))
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    width(40.px)
                    height(40.px)
                    property("transition", "background-color 0.2s ease")
                }
                onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = colors.surfaceHover }
                onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "transparent" }
            }) {
                if (isDark) IconSun(size = 20) else IconMoon(size = 20)
            }

            // User menu
            if (userEmail != null) {
                Div({
                    style {
                        property("position", "relative")
                    }
                }) {
                    Button({
                        onClick { showUserMenu = !showUserMenu }
                        attr("aria-label", "User menu")
                        style {
                            background("none")
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            cursor("pointer")
                            padding(Spacing.sm, Spacing.lg)
                            borderRadius(BorderRadius.md)
                            color(Color(colors.text))
                            fontSize(Typography.sm)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            gap(Spacing.sm)
                            property("transition", "background-color 0.2s ease")
                        }
                        onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = colors.surfaceHover }
                        onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "transparent" }
                    }) {
                        val displayEmail = if (userEmail.length > 24) userEmail.take(22) + "..." else userEmail
                        Text(displayEmail)
                    }

                    // Dropdown menu
                    if (showUserMenu) {
                        Div({
                            style {
                                property("position", "absolute")
                                property("top", "100%")
                                property("right", "0")
                                marginTop(Spacing.xs)
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                property("box-shadow", "0 5px 15px rgba(40,60,80,0.15)")
                                property("z-index", "1030")
                                minWidth(160.px)
                                overflow("hidden")
                            }
                        }) {
                            Button({
                                onClick {
                                    showUserMenu = false
                                    onLogout()
                                }
                                attr("aria-label", "Logout")
                                style {
                                    width(100.percent)
                                    background("none")
                                    border(0.px)
                                    cursor("pointer")
                                    padding(Spacing.md, Spacing.lg)
                                    color(Color(colors.danger))
                                    fontSize(Typography.sm)
                                    display(DisplayStyle.Flex)
                                    alignItems(AlignItems.Center)
                                    gap(Spacing.sm)
                                    textAlign("left")
                                    property("transition", "background-color 0.2s ease")
                                }
                                onMouseOver { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = colors.surfaceHover }
                                onMouseOut { (it.target as? org.w3c.dom.HTMLElement)?.style?.backgroundColor = "transparent" }
                            }) {
                                IconLogOut(size = 16)
                                Text("Logout")
                            }
                        }
                    }
                }
            }
        }
    }

    // Click-away listener for user menu
    if (showUserMenu) {
        Div({
            onClick { showUserMenu = false }
            style {
                property("position", "fixed")
                property("top", "0")
                property("left", "0")
                width(100.vw)
                height(100.vh)
                property("z-index", "1024")
            }
        })
    }
}
